/* =====================================================================================
   MLR review panel: documents, comments and reviewer assignment.

   Works on the file people actually have. DOCX, PPTX and PDF all upload and all attract
   comments; the comment thread does not need a rendered page to be useful. Where a page
   number is given it is recorded, so the same comments pin to a rendition later without
   anyone re-entering them.
   ===================================================================================== */

import { useEffect, useState } from 'react';
import {
  fetchTasks, fetchComments, assignReview, decideReview, addComment, resolveComment,
  CAPACITIES, SEVERITIES, type ReviewTask, type Comment,
} from '../lib/review';
import { fetchColleagues, type Colleague } from '../lib/chorus';
import { Attachments } from './Attachments';
import './review.css';
import { onFail } from '../lib/errors';

export function ReviewPanel({ assetId, versionId }: {
  assetId: string; versionId?: string | null;
}) {
  const [tasks, setTasks] = useState<ReviewTask[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [people, setPeople] = useState<Colleague[]>([]);
  const [me, setMe] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [a, setA] = useState({ assigneeId: '', capacity: 'medical', dueOn: '' });
  const [c, setC] = useState({ body: '', severity: 'comment', capacity: '', page: '' });
  const [err, setErr] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const reload = () => setNonce((n) => n + 1);

  useEffect(() => {
    fetchTasks(assetId).then(setTasks).catch((e) => setErr(String(e.message ?? e)));
    fetchComments(assetId).then(setComments).catch(onFail('Review comments', () => setComments([])));
    fetchColleagues().then(setPeople).catch(onFail('Review comments', () => setPeople([])));
    import('../lib/supabase').then((m) => m.currentMember()).then((x) => setMe(x?.id ?? null)).catch(() => {});
  }, [assetId, nonce]);

  const openBlockers = comments.filter((x) => x.severity === 'blocker' && x.status === 'open');
  const myTask = tasks.find((t) => t.assignee_id === me && (t.status === 'open' || t.status === 'in_progress'));

  async function post() {
    setErr(null);
    try {
      await addComment({
        assetId, versionId, body: c.body, severity: c.severity,
        capacity: c.capacity || undefined,
        page: c.page ? Number(c.page) : null,
      });
      setC({ body: '', severity: 'comment', capacity: '', page: '' });
      reload();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  return (
    <div className="rv">
      {err && <p className="block-error" role="alert">{err}</p>}

      {/* The file itself. DOCX, PPTX and PDF are all accepted; the review does not wait on a
          rendition, because a Word document has no page geometry until it is rendered. */}
      <Attachments
        objectType="asset" objectId={assetId} bucket="assets"
        label="Proofs and source files"
        roles={['draft', 'attachment', 'rendition', 'signed']}
        accept=".pdf,.docx,.doc,.pptx,.ppt,.xlsx,.png,.jpg,.jpeg,.svg,.ai,.indd"
      />

      {/* ---- reviewers ---- */}
      <section className="rv-block">
        <header className="rv-head">
          <h3>MLR reviewers</h3>
          <button className="btn-compose" onClick={() => setAssigning(!assigning)}>
            {assigning ? 'Cancel' : 'Assign a reviewer'}
          </button>
        </header>

        {assigning && (
          <div className="rv-assign">
            <select value={a.assigneeId} onChange={(e) => setA({ ...a, assigneeId: e.target.value })}>
              <option value="">Who…</option>
              {people.map((p) => <option key={p.id} value={p.id}>{p.full_name ?? p.email}</option>)}
            </select>
            {/* Capacity, not job title. The same person may review in two capacities and each
                one is a separate decision. */}
            <select value={a.capacity} onChange={(e) => setA({ ...a, capacity: e.target.value })}>
              {CAPACITIES.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
            <input type="date" value={a.dueOn} onChange={(e) => setA({ ...a, dueOn: e.target.value })} />
            <button className="btn-save" disabled={!a.assigneeId}
                    onClick={() => assignReview({ assetId, versionId, assigneeId: a.assigneeId,
                      capacity: a.capacity, dueOn: a.dueOn || undefined })
                      .then(() => { setAssigning(false); setA({ assigneeId: '', capacity: 'medical', dueOn: '' }); reload(); })
                      .catch((e) => setErr(String(e.message ?? e)))}>
              Assign
            </button>
          </div>
        )}

        {tasks.length === 0 && !assigning && (
          <p className="att-empty">Nobody assigned yet. Medical, legal and regulatory each answer
            a different question, so each needs asking separately.</p>
        )}

        <div className="rv-tasks">
          {tasks.map((t) => (
            <div key={t.id} className={`rv-task rv-${t.status}`}>
              <span className="pill mute">{t.capacity}</span>
              <strong>{t.assignee_name ?? 'someone'}</strong>
              <span className={`pill ${t.status === 'approved' ? 'ok'
                : t.status === 'changes_required' || t.status === 'declined' ? 'risk' : 'warn'}`}>
                {t.status.replace(/_/g, ' ')}
              </span>
              {t.due_on && (
                <span className={new Date(t.due_on) < new Date() && t.status === 'open' ? 'rv-overdue' : 'muted'}>
                  due {new Date(t.due_on).toLocaleDateString('en-GB')}
                </span>
              )}
              {t.decision_note && <span className="rv-note">{t.decision_note}</span>}
            </div>
          ))}
        </div>

        {/* Only the assigned reviewer sees their own decision controls. */}
        {myTask && (
          <div className="rv-decide">
            <span>Your {myTask.capacity} review:</span>
            <button className="btn-save"
                    onClick={() => decideReview(myTask.id, 'approved').then(reload)
                      .catch((e) => setErr(String(e.message ?? e)))}>
              Approve
            </button>
            <button className="btn-cancel"
                    onClick={() => decideReview(myTask.id, 'changes_required').then(reload)
                      .catch((e) => setErr(String(e.message ?? e)))}>
              Changes required
            </button>
            {openBlockers.some((b) => b.author_id === me) && (
              <span className="rv-warn">
                You have an unresolved blocker on this version. The database will refuse an
                approval until you resolve or withdraw it.
              </span>
            )}
          </div>
        )}
      </section>

      {/* ---- comments ---- */}
      <section className="rv-block">
        <header className="rv-head">
          <h3>Comments {comments.length > 0 && <span className="att-count">{comments.length}</span>}</h3>
          {openBlockers.length > 0 && (
            <span className="pill risk">{openBlockers.length} open blocker{openBlockers.length > 1 ? 's' : ''}</span>
          )}
        </header>

        <div className="rv-compose">
          <textarea rows={2} value={c.body} placeholder="Comment on this asset"
                    onChange={(e) => setC({ ...c, body: e.target.value })} />
          <div className="rv-compose-row">
            <select value={c.severity} onChange={(e) => setC({ ...c, severity: e.target.value })}>
              {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={c.capacity} onChange={(e) => setC({ ...c, capacity: e.target.value })}>
              <option value="">no capacity</option>
              {CAPACITIES.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
            <input type="number" min={1} placeholder="page" value={c.page} style={{ width: '5rem' }}
                   onChange={(e) => setC({ ...c, page: e.target.value })} />
            <button className="btn-save" disabled={!c.body.trim()} onClick={post}>Comment</button>
          </div>
        </div>

        {comments.map((x) => (
          <article key={x.id} className={`rv-comment sev-${x.severity} ${x.status !== 'open' ? 'done' : ''}`}>
            <div className="rv-comment-head">
              <strong>{x.author_name ?? 'A colleague'}</strong>
              <span className={`pill ${x.severity === 'blocker' ? 'risk'
                : x.severity === 'major' ? 'warn' : x.severity === 'praise' ? 'ok' : 'mute'}`}>
                {x.severity}
              </span>
              {x.capacity && <span className="pill info">{x.capacity}</span>}
              {x.page && <span className="muted">p.{x.page}</span>}
              <span className="rv-when">
                {new Date(x.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              </span>
            </div>
            <p>{x.body}</p>
            <div className="rv-comment-foot">
              {x.status === 'open' ? (
                <>
                  <button className="link-btn" onClick={() => resolveComment(x.id, 'addressed').then(reload)}>
                    Mark addressed
                  </button>
                  <button className="link-btn" onClick={() => resolveComment(x.id, 'withdrawn').then(reload)}>
                    Withdraw
                  </button>
                </>
              ) : (
                <span className="pill ok">{x.status}</span>
              )}
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}
